import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, timer } from 'rxjs';
import axios from 'src/app/Service/axios';
import { GurdServiceService } from 'src/app/Service/gurd-service.service';
import { NotificationService } from 'src/app/Service/notification.service';
import { OtherService } from 'src/app/Service/other.service';
import { CameraStream, SurveillanceVehicle } from './custom-surveillance.models';

@Component({
  selector: 'app-custom-surveillance',
  templateUrl: './custom-surveillance.component.html',
  styleUrls: ['./custom-surveillance.component.css'],
})
export class CustomSurveillanceComponent implements OnInit, OnDestroy {
  unitId = '';
  vehicle: SurveillanceVehicle | null = null;
  loading = true;
  error = '';
  readonly cameras: CameraStream[] = ([0, 1] as const).map(channel => ({
    channel,
    title: channel === 0 ? 'دوربین جلو' : 'دوربین کابین',
    enabled: false, busy: false, loading: false,
    url: '', error: '', attempts: 0, version: 0,
    player: { strUnitID: '', strUnitId: '' },
  }));
  private routeSubscription?: Subscription;
  private destroyed = false;
  private loadVersion = 0;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private guard: GurdServiceService,
    private notify: NotificationService,
    private otherService: OtherService,
  ) {}

  ngOnInit(): void {
    this.routeSubscription = this.route.params.subscribe(({ id }) => {
      this.unitId = id;
      this.cameras.forEach(camera => {
        camera.version++;
        camera.poll?.unsubscribe();
        Object.assign(camera, { enabled: false, busy: false, loading: false, url: '', error: '' });
        camera.player = { strUnitID: id, strUnitId: `${id}-${camera.channel}` };
      });
      this.loadVehicle();
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.routeSubscription?.unsubscribe();
    this.cameras.forEach(camera => camera.poll?.unsubscribe());
  }

  async loadVehicle(): Promise<void> {
    const version = ++this.loadVersion;
    this.loading = true;
    this.error = '';
    this.vehicle = null;
    try {
      const vehicle = await this.request(`GetCar?strUnitID=${encodeURIComponent(this.unitId)}`);
      if (this.destroyed || version !== this.loadVersion) return;
      this.vehicle = vehicle;
      const status = await this.request('Dashcam_CheckOnlineFull');
      if (this.destroyed || version !== this.loadVersion) return;
      this.cameras.forEach(camera => {
        const url = this.onlineUrl(status, camera);
        camera.url = url;
        camera.enabled = !!url;
      });
    } catch (error) {
      if (!this.destroyed && version === this.loadVersion) this.error = this.errorMessage(error);
    } finally {
      if (!this.destroyed && version === this.loadVersion) this.loading = false;
    }
  }

  async toggleCamera(camera: CameraStream): Promise<void> {
    const version = ++camera.version;
    camera.poll?.unsubscribe();
    camera.error = '';
    camera.busy = true;
    camera.loading = camera.enabled;
    try {
      if (!camera.enabled) {
        await this.request('Dashcam_CancelVideoStream', { tiChannel: camera.channel });
        if (this.destroyed || camera.version !== version) return;
        camera.url = '';
      } else {
        const status = await this.request('Dashcam_CheckOnlineFull');
        if (this.destroyed || camera.version !== version) return;
        camera.url = this.onlineUrl(status, camera);
        if (!camera.url) {
          const commands = await this.request('Dashcam_SendCommand4VideoStream', {
            tiChannel: camera.channel,
            ClientId: this.guard.decrypt(localStorage.getItem('clientId')!) || '',
            Token: this.guard.decrypt(localStorage.getItem('token')!) || '',
          });
          if (this.destroyed || camera.version !== version) return;
          if (commands[0]?.iDeviceCommand == null) throw new Error('درخواست اتصال به دوربین ثبت نشد.');
          camera.commandId = commands[0].iDeviceCommand;
          camera.attempts = 0;
          await this.pollStream(camera, version);
        }
      }
    } catch (error) {
      if (!this.destroyed && camera.version === version) {
        camera.enabled = !!camera.url;
        camera.error = this.errorMessage(error);
        this.notify.showError(camera.error);
      }
    } finally {
      if (!this.destroyed && camera.version === version) {
        camera.busy = false;
        camera.loading = camera.enabled && !camera.url;
      }
    }
  }

  private async pollStream(camera: CameraStream, version: number): Promise<void> {
    try {
      const stream = await this.request('Dashcam_GetLiveStreamURL', {
        tiChannel: camera.channel,
        iDeviceCommand: camera.commandId,
      });
      if (this.destroyed || camera.version !== version || !camera.enabled) return;
      camera.url = stream.strFLVURL || stream.strHLSURL || '';
      if (camera.url) {
        camera.loading = false;
        return;
      }
      if (camera.attempts++ >= 6) throw new Error('پاسخی از دوربین دریافت نشد. دوباره تلاش کنید.');
      camera.poll = timer(10000).subscribe(() => this.pollStream(camera, version));
    } catch (error) {
      if (!this.destroyed && camera.version === version) {
        camera.enabled = false;
        camera.loading = false;
        camera.error = this.errorMessage(error);
        this.notify.showError(camera.error);
      }
    }
  }

  private onlineUrl(status: any, camera: CameraStream): string {
    const channel = camera.channel + 1;
    return status.Online && status[`Ch${channel}`] == 1
      ? status[`strCh${channel}FLVURL`] || '' : '';
  }

  private async request(endpoint: string, payload: object = {}): Promise<any> {
    try {
      const { data } = await axios.post(endpoint, this.otherService.stringNumberToNumber({
        iSystemUser: this.guard.decrypt(localStorage.getItem('ID')!),
        strSession: this.guard.decrypt(localStorage.getItem('Session')!),
        strUnitID: this.unitId,
        ...payload,
      }));
      const response = typeof data === 'string' ? JSON.parse(data) : data;
      if (!response) throw new Error('پاسخ نامعتبر از سامانه دریافت شد.');
      if (response.ISSessionExpired == true) {
        this.otherService.removeLocalStorage();
        this.router.navigate(['login'], { queryParams: { error: 'x12q1dss1110' } });
        throw new Error('لطفا وارد سیستم شوید');
      }
      if (response.iResult == 0) throw new Error(response.strError || 'انجام درخواست ناموفق بود.');
      return response;
    } catch (error) {
      throw new Error(error instanceof Error && 'isAxiosError' in error
        ? 'ارتباط با سامانه برقرار نشد. دوباره تلاش کنید.' : this.errorMessage(error));
    }
  }

  private errorMessage(error: unknown): string {
    return error instanceof Error ? error.message : 'انجام درخواست ناموفق بود. دوباره تلاش کنید.';
  }
  
}
